import { observer } from 'mobx-react';
import GeneralLoader from '../../../../../components/loaders/GeneralLoader';
import { Fragment } from 'react';

/**
 * ProfilerNameSearchResults - Reusable results list for name-based searches
 *
 * Generic results list component that works for any entity type (companies, individuals, etc.).
 * Delegates individual result rendering to a custom ResultItemComponent.
 *
 * IMPORTANT - PARENT CONTAINER REQUIREMENTS (TD-1900):
 * For scroll behavior to work correctly, this component MUST be placed inside a parent
 * container with the following CSS properties:
 *   - display: flex; flex-direction: column;
 *   - flex: 1; (to allow child to grow)
 *   - height: 100%; min-height: 0; (to enable flex scrolling)
 * See: src/styles/search-form.less:1330-1334 for CSS contract documentation.
 * Example parent: .profiler-search-by-name-wrapper
 *
 * @param {Array} results - Array of search results
 * @param {React.Component} ResultItemComponent - Component to render each result item
 * @param {Object} resultItemProps - Additional props passed to each ResultItemComponent
 * @param {boolean} isTyping - Whether user is currently typing
 * @param {boolean} isLoading - Whether search is in progress
 * @param {string} loadError - Error message if search failed
 * @param {string} searchKey - Current search key (for highlighting)
 * @param {number} minSearchLength - Minimum characters before showing "no results" (default: 3)
 */
const ProfilerNameSearchResults = observer(
  ({
    results = [],
    ResultItemComponent,
    resultItemProps = {},
    isTyping = false,
    isLoading = false,
    loadError = null,
    searchKey = '',
    minSearchLength = 3
  }) => {
    const showNoResults =
      !isTyping &&
      !isLoading &&
      !loadError &&
      !results.length &&
      searchKey.length >= minSearchLength;
    const showLoader = !isTyping && isLoading;
    const showError = !isTyping && !!loadError;
    const showResults = !isTyping && !!results.length;

    return (
      <Fragment>
        {showNoResults && <div className="no-profiler-name-results">No results found</div>}
        {showLoader && <GeneralLoader />}
        {showError && <div className="error">{loadError}</div>}
        {showResults && (
          <div className="profiler-name-search-results">
            {results.map((result) => (
              <ResultItemComponent
                key={result.id}
                result={result}
                searchKey={searchKey}
                {...resultItemProps}
              />
            ))}
          </div>
        )}
      </Fragment>
    );
  }
);

export default ProfilerNameSearchResults;
